import * as React from "react";

import { ElementHtmlAttributes, getAttributes } from "../common/common";
import { UpdateCommandItem, UpdateControl, TUFActionDisplayStyle, UFUpdateControl } from "../common/communication.base";
import { NclCommandItem, NclBreaker, NclImage, NclInnerSection } from "../common/components.ncl";
import { SubMenuIndicator } from "./K2Action";
import { K2ComponentState, withContext, WithContextPlacementProps, StyleHelper, AcquireControl } from "./k2hoc";
import { K2Img } from "./K2Image";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";
import { K2TruncateText } from "./K2TruncateText";

const breakerRules = {
  baseStyle: (props: K2RuleWithVCXProps) => ({
    flex: "1 0 100%",
    small: {
      flex: "0 1 auto",
    },
  }),
};

class _ControlBreaker extends React.PureComponent<WithContextPlacementProps, K2ComponentState<UFUpdateControl>> {
  private control: NclBreaker;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => ctrl instanceof NclBreaker) as NclBreaker;
    this.state = { data: this.control.init(this) as UFUpdateControl, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UFUpdateControl>) => {
      return { data: state as UFUpdateControl };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  render() {
    return (
      <div
        style={StyleHelper(this.control, this.props.style)}
        className={this.props.renderer.renderRule(breakerRules.baseStyle, { vcx: this.control.VCX })}
      ></div>
    );
  }
}

export const K2ControlBreaker = withContext(_ControlBreaker);

type K2BtnRuleProps = K2RuleWithVCXProps & {
  checked: boolean;
  disabled: boolean;
};

type K2StyleRuleProps = K2RuleWithVCXProps & {
  displayStyle: TUFActionDisplayStyle;
  heightIcon?: number;
};
const actionRules = {
  baseStyle: (props: K2StyleRuleProps) => ({
    height: props.displayStyle == TUFActionDisplayStyle.ufadsButtonTile ? "100%" : "auto",
    writingMode: "horizontal-tb" as "horizontal-tb",
    small: {
      justifyContent: "center" as "center",
      height: props.heightIcon > 0 ? props.heightIcon + "px" : 1.5 * props.vcx.LabelControl.getHeight(1) + "px",
    },
  }),
  contentStyle: (props: K2StyleRuleProps) => ({
    alignItems: "center",
    flexDirection: props.displayStyle == TUFActionDisplayStyle.ufadsButtonLine ? ("row" as "row") : ("column" as "column"),
    small: {
      flexDirection: "column" as "column",
    },
  }),
  buttonStyle: (props: K2BtnRuleProps) => ({
    padding: props.vcx.sizeMap(3) + "px",
    width: "100%",
    background: props.checked ? props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1) : "transparent",
    color: "inherit",
    border: props.checked ? props.vcx.sizeMap(2) + "px solid " + props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame3) : "none",
    position: "relative" as const,
    opacity: props.disabled ? "0.4" : null,
    cursor: props.disabled ? "not-allowed" : null,
    small: {
      padding: `0px ${props.vcx.sizeMap(3)}px`,
    },
  }),
  captionStyle: (props: K2RuleWithVCXProps) => ({
    color: props.vcx.getColor(props.vcx.Data.ColorMap.ContentNormalColorFrg),
    alignSelf: "center",
    small: {
      display: "none",
    },
  }),
  placeholder: (props: K2StyleRuleProps) => ({
    display: "flex",
    ">div": {
      width:
        props.heightIcon > 0
          ? props.heightIcon + "px"
          : props.vcx.LabelControl.getHeight(1) * (props.displayStyle == TUFActionDisplayStyle.ufadsButtonTile ? 3 : 1.2) + "px",
      height:
        props.heightIcon > 0
          ? props.heightIcon + "px"
          : props.displayStyle === TUFActionDisplayStyle.ufadsButtonTile
          ? props.vcx.sizeMap(27) + "px"
          : props.vcx.sizeMap(17) + "px",
      small: {
        width: 1.5 * props.vcx.LabelControl.getHeight(1) + "px",
        height: props.heightIcon > 0 ? props.heightIcon + "px" : 1.5 * props.vcx.LabelControl.getHeight(1) + "px",
      },
    },
  }),
};

interface ActionProps extends WithContextPlacementProps {
  color?: string;
  captionLinesCount?: number;
  heightIcon?: number;
  updateVisible?: () => void;
}

class _Action extends React.PureComponent<ActionProps, K2ComponentState<UpdateCommandItem>> {
  private control: NclCommandItem;

  constructor(props: ActionProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclCommandItem;
    }) as NclCommandItem;
    this.state = { data: this.control.init(this) as UpdateCommandItem, vcxVersion: -1 };
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateCommandItem>) => {
      return { data: state as UpdateCommandItem };
    });

    if (this.props.updateVisible && this.control.Parent instanceof NclInnerSection && state.Visible) {
      this.props.updateVisible();
    }
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  render() {
    let addAttributes: ElementHtmlAttributes = getAttributes(this.state.data);

    let cls = "";
    if (this.props.className) {
      cls = " " + this.props.className;
    }

    return (
      <div
        style={StyleHelper(this.control, Object.assign({ width: "auto" }, this.props.style))}
        className={
          this.props.renderer.renderRule(actionRules.baseStyle, {
            vcx: this.control.VCX,
            displayStyle: this.control.Ncl.FrgtData.DisplayStyle,
            heightIcon: this.props.heightIcon,
          }) + cls
        }
      >
        <button
          data-k2-test-id={this.control.MetaData.Name}
          className={this.props.renderer.renderRule(actionRules.buttonStyle, {
            vcx: this.control.VCX,
            checked: this.state.data.Checked,
            disabled: !this.state.data.Enabled,
          })}
          onClick={this.handleClick}
          {...addAttributes}
        >
          <div
            className={this.props.renderer.renderRule(actionRules.contentStyle, {
              vcx: this.control.VCX,
              displayStyle: this.control.Ncl.FrgtData.DisplayStyle,
            })}
          >
            <div
              className={this.props.renderer.renderRule(actionRules.placeholder, {
                vcx: this.control.VCX,
                displayStyle: this.control.Ncl.FrgtData.DisplayStyle,
                heightIcon: this.props.heightIcon,
              })}
            >
              {this.state.data.GlyphId && (
                <K2Img
                  key={this.control.MetaData.ControlUID + "_icon"}
                  glyphId={this.state.data.GlyphId}
                  vcx={this.control.VCX}
                  strokeColor={this.props.color}
                  height={NclImage.isSVG(this.state.data.GlyphId) ? null : 48}
                />
              )}
            </div>
            {this.control.Ncl.FrgtData.DisplayStyle != TUFActionDisplayStyle.ufadsButtonSmall && this.state.data.Title && (
              <K2TruncateText
                key={this.control.MetaData.ControlUID + "_caption"}
                className={
                  this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX) +
                  " " +
                  this.props.renderer.renderRule(actionRules.captionStyle, { vcx: this.control.VCX })
                }
              >
                {this.state.data.Title}
              </K2TruncateText>
            )}
          </div>
          {this.state.data.SubMenuIndicatorVisible && <SubMenuIndicator vcx={this.control.VCX} strokeColor={this.props.color} />}
        </button>
      </div>
    );
  }

  private handleClick = () => {
    this.control.executeCommand();
  };
}

export const K2RibbonAction = withContext(_Action);
